import React from 'react';
import classNames from 'classnames';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';
import { useId } from '@reach/auto-id';

import QuestionCircleSvg from '../../../design-system/icons/core/question-circle.svg?component';
import { RCIcon } from '../RCIcon';

interface IProps {
  placement?: Placement; // top | bottom | left | right | top-start | top-end | bottom-start | bottom-end
  children?: React.ReactNode;
  text: string;
  type?: 'icon' | 'underline' | 'plain';
  stopPropagation?: boolean;
}

export const MOUSEOUT_BUFFER_MS = 100;
export const ESCAPE_KEY = 27;
const ENTER_KEY = 13;
const SPACE_KEY = 32;

const ACTIVE = 'active';
const CLICKED = 'clicked';
const MOUSE_IN = 'mouse_in';
const MOUSE_OUT = 'mouse_out';
const NONE = 'none';

function reducer(state, action) {
  // only go to MOUSE_IN state if tooltip is not already visible
  if (state === NONE && action === MOUSE_IN) {
    return MOUSE_IN;
  }

  // only hide via mouse_out if tootlip was displayed via mouse_in
  if (state === MOUSE_IN && action === MOUSE_OUT) {
    return NONE;
  }

  if ([MOUSE_IN, MOUSE_OUT].includes(action)) {
    return state;
  }

  // ACTIVE can be reached via click, keyboard interaction or focus
  // clicking a trigger that is already displaying a tooltip will toggle the tooltip off
  if (action === CLICKED) {
    return (state === ACTIVE) ? NONE : ACTIVE;
  }

  // setting the tooltip to ACTIVE OR NONE will always force it to that state regardless of the current state
  return action;
}

function shouldClose(id) {
  return !document.querySelector(`#${id}:hover`) && !document.querySelector(`[aria-describedby="${id}"]:hover`);
}

// call child handler if exists and tooltip handler
function composeEventHandlers<EventType extends React.SyntheticEvent | Event>(
  childHandler: ((event: EventType) => any) | undefined,
  tooltipHandler: (event: EventType) => any,
): (event: EventType) => any {
  return (event) => {
    if (childHandler) {
      childHandler(event);
    }
    tooltipHandler(event);
  };
}

interface IBaseTriggerElementProps {
  children?: React.ReactNode;
  text: string;
  type?: 'icon' | 'underline' | 'plain';
}
// Default <button> if children is non-existent or a string
function baseTriggerElement({ children, type, text }: IBaseTriggerElementProps) {
  if (children && typeof children !== 'string') {
    return children;
  }

  if (type === 'plain') {
    return (
      <button type="button" className="tooltip__trigger--button-reset">
        {children}
      </button>
    );
  }

  return (
    <button
      type="button"
      className="tooltip__trigger--button-reset"
      aria-label={!children ? text : undefined}
    >
      {!!children && (
        <span className="tooltip__trigger__inner">
          {children}
        </span>
      )}
      {(type === 'icon') && (
        <RCIcon svgComponent={QuestionCircleSvg} inline />
      )}
    </button>
  );
}

export  function RCTooltip({
  children,
  text,
  placement = 'top',
  type = 'icon',
  stopPropagation = false,
}: IProps) {
  const [state, dispatch] = React.useReducer(reducer, NONE);
  const visible = state !== NONE;

  const id = useId();
  const tooltipId = React.useMemo(() => `tooltip-${id}`, [id]);

  const referenceElement = React.useRef<HTMLInputElement>();
  const [popperElement, setPopperElement] = React.useState(null);
  const [arrowElement, setArrowElement] = React.useState(null);
  const [closeTimeout, setCloseTimeout] = React.useState(null);

  const { styles, attributes } = usePopper(referenceElement?.current, popperElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  React.useEffect(() => {
    // globally close tooltip on any click outside of the trigger element
    // or if the user hits the ESC key
    function handleDocumentClick(event) {
      if (referenceElement?.current?.contains(event.target) || popperElement?.contains(event.target)) {
        return;
      }
      dispatch(NONE);
    }

    function handleKeyDown(event) {
      if (event.keyCode === ESCAPE_KEY) {
        dispatch(NONE);
      }
    }

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleKeyDown);

    };
  }, [referenceElement, popperElement]);

  function onClick(e) {
    if (stopPropagation) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  const bufferedHoverClose = React.useCallback(() => {
    // Wait before immediately closing the tooltip on mouse leave
    // if the user is hovering over the trigger or the tooltip container after the buffer, don't close the tooltip
    clearTimeout(closeTimeout);
    const timeoutId = setTimeout(
      () => {
        if (shouldClose(tooltipId)) {
          dispatch(MOUSE_OUT);
        }
      },
      MOUSEOUT_BUFFER_MS,
    );

    setCloseTimeout(timeoutId);
  }, [closeTimeout, tooltipId]);

  const onBlur = React.useCallback(() => {
    clearTimeout(closeTimeout);
    dispatch(NONE);
  }, [closeTimeout]);

  const onKeyDown = React.useCallback((event) => {
    if ([SPACE_KEY, ENTER_KEY].includes(event.keyCode)) {
      dispatch(CLICKED);
    }
  }, []);

  const triggerEl = baseTriggerElement({ children, type, text }) as JSX.Element;

  // ensure that if children exists, it's only a single node
  let child;
  if (children && typeof children !== 'string') {
    child = React.Children.only(children) as any;
  }

  const triggerClasses = classNames(
    'tooltip__trigger',
    child?.props?.className,
    triggerEl?.props?.className,
    {
      'tooltip__trigger--icon': type === 'icon',
      'tooltip__trigger--underline': type === 'underline',
    },
  );

  // mouse, keyboard handlers, and accessibility props to copy to the trigger element
  const triggerProps = {
    ref: referenceElement,
    onMouseEnter: composeEventHandlers(child?.props?.onMouseEnter, () => dispatch(MOUSE_IN)),
    onMouseLeave: composeEventHandlers(child?.props?.onMouseLeave, bufferedHoverClose),
    onFocus: composeEventHandlers(child?.props?.onFocus, () => dispatch(ACTIVE)),
    onBlur: composeEventHandlers(child?.props?.onBlur, onBlur),
    onMouseDown: composeEventHandlers(child?.props?.onMouseDown, () => dispatch(CLICKED)),
    onKeyDown: composeEventHandlers(child?.props?.onKeyDown, onKeyDown),
    onClick: composeEventHandlers(child?.props?.onClick, onClick),
    'aria-describedby': visible ? tooltipId : undefined,
    className: triggerClasses,
    tabIndex: child?.props?.tabIndex ?? 0,
  };

  return (
    <>
      {React.cloneElement(triggerEl, triggerProps)}

      {visible && (
        <span
          id={tooltipId}
          ref={setPopperElement}
          style={styles.popper}
          className="tooltip__popper"
          role="tooltip"
          onMouseLeave={bufferedHoverClose}
          {...attributes.popper}
        >
          {text}
          <span ref={setArrowElement} style={styles.arrow} className="tooltip__arrow" />
        </span>
      )}
    </>
  );
}
